import {
	type LoaderFunctionArgs,
	json,
	type ActionFunctionArgs,
} from '@remix-run/node'
import {
	useFetcher,
	useLoaderData,
	useLocation,
	useSearchParams,
} from '@remix-run/react'
import { parseAsBoolean } from 'inngest/helpers/env'
import { useEffect } from 'react'
import { GeneralErrorBoundary } from '#app/components/error-boundary.js'
import { Branding } from '#app/components/powered.js'
import Customizer from '#app/components/tiptap/customizer/customizer.js'
import { useCustomizer } from '#app/components/tiptap/hooks/useCustomizer.js'
import { Skeleton } from '#app/components/ui/skeleton.js'
import { PAGE_TYPES } from '#app/utils/constants.js'
import { getComputedData } from '#app/utils/customizer.server.js'
import { prisma } from '#app/utils/db.server.js'
import { generatePageStyles, injectScripts } from '#app/utils/styling.js'
import { signup } from '#app/utils/sub.model.js'
import { useSendIframeHeight } from '#app/utils/useIframeHeight.js'
import { FORM_KEY } from './$url.($referrer)'

export async function loader({ params }: LoaderFunctionArgs) {
	const url = params.url

	const list = await prisma.list.findFirstOrThrow({
		where: { OR: [{ url }, { id: url }] },
		include: {
			pages: { where: { type: PAGE_TYPES.SIGNUP }, include: { theme: true } },
			settings: true,
			domain: true,
			theme: true,
			metrics: true,
			user: {
				select: {
					isPro: true,
				},
			},
		},
	})

	const { listData } = await getComputedData({ list })

	return json({ list: { ...list, ...listData }, isPro: list.user.isPro })
}

export async function action({ params, request }: ActionFunctionArgs) {
	const { res } = await (await signup(params, request)).json()

	if (res.redirect) return json({ success: true })
	else return json(res)
}

export default function ProjectPage() {
	const { list, isPro } = useLoaderData<typeof loader>()

	const fetcher = useFetcher({ key: FORM_KEY })

	const page = list.pages[0]

	const content = page.content

	const theme = page.theme

	const [searchParams] = useSearchParams()

	const transparent = parseAsBoolean(searchParams.get('transparent'))

	const preview = parseAsBoolean(searchParams.get('preview'))

	const fullpage = parseAsBoolean(searchParams.get('fullpage'))

	useEffect(() => {
		// @ts-ignore
		injectScripts({ page: page ?? {}, list })
	}, [page, list])

	const classNames =
		fullpage && !preview
			? 'mx-auto h-full min-h-screen w-full max-w-[516px] content-center px-[24px] pb-[128px] pt-[128px]'
			: ''

	const embedStyleOverride =
		fullpage && !preview
			? ''
			: `
		.ProseMirror > * {
				margin-left: auto !important;
				margin-right: auto !important;
				max-width: 420px !important;
				transition-property: all;
		}
		.ProseMirror > *:last-child > * {
				margin-bottom: 0 !important;
		}`

	useSendIframeHeight(!fullpage && !preview)

	const { editor } = useCustomizer({
		content,
		// @ts-ignore
		data: { list },
		mode: 'page',
		editable: false,
	})

	if (!editor) {
		return (
			<div className="flex flex-col gap-5">
				<Skeleton className="h-[100px] w-full rounded-xl bg-white/30" />
			</div>
		)
	}

	return (
		<>
			<div
				style={{
					background: transparent
						? 'transparent !important'
						: (theme?.backgroundColor ?? ''),
				}}
				className="no-scrollbar"
			>
				{theme && (
					<style
						dangerouslySetInnerHTML={{
							__html: generatePageStyles(theme) + embedStyleOverride,
						}}
					/>
				)}

				<fetcher.Form method="post" className={classNames}>
					{editor && <Customizer editor={editor} />}
				</fetcher.Form>

				{fullpage && <Branding isPro={isPro} className="fixed" />}
			</div>
		</>
	)
}

export function ErrorBoundary() {
	const location = useLocation()
	return (
		<GeneralErrorBoundary
			statusHandlers={{
				404: () => (
					<div className="flex flex-col gap-6">
						<div className="flex flex-col gap-3">
							<h1 className="whitespace-pre-wrap break-all text-body-lg">
								We can't find this page:
							</h1>
							<pre className="break-all text-body-lg">{location.pathname}</pre>
						</div>
					</div>
				),
			}}
		/>
	)
}
